/**
 * The notebook: notes that belong to nothing.
 *
 * Two routes, not a split pane. A list and a note are different screens on a
 * phone, and pretending otherwise means a sidebar that has to collapse, a
 * selection that has to survive it, and a back button that does the wrong
 * thing. `/notes` lists; `/notes/:noteId` is one note.
 *
 * The detail page lives in its own file and is NOT exported from here, because
 * it pulls in TipTap. Exporting both from one module makes the whole file
 * statically reachable, Vite cannot split it, and the entire editor lands back
 * in the main bundle.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { StandaloneNoteSummary } from '@/api/types'
import { useToast } from '@/components/Toast'
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui'
import { formatTimestamp, pluralize } from '@/lib/format'


export function NotesPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [notes, setNotes] = useState<StandaloneNoteSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)

  const load = useCallback(async (q: string) => {
    try {
      setNotes(await api.standaloneNotes.list(q || undefined))
      setError(null)
    } catch (caught) {
      if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load(search), search ? 250 : 0)
    return () => clearTimeout(timer)
  }, [search, load])

  async function createNote() {
    if (creating) return
    setCreating(true)
    try {
      // Created empty and opened straight away: the editor needs a real id
      // before an image can be pasted into it. An untouched note is deleted
      // again when you leave it, so this cannot leave litter.
      const note = await api.standaloneNotes.create()
      navigate(`/notes/${note.id}`)
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not create a note.')
      setCreating(false)
    }
  }

  if (loading) return <LoadingBlock label="Loading notes" />
  if (error) return <ErrorState message={error} onRetry={() => void load(search)} />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <input
          type="search"
          className="field flex-1"
          placeholder="Search notes..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Search notes"
        />
        <button
          type="button"
          className="btn btn-primary shrink-0"
          onClick={() => void createNote()}
          disabled={creating}
        >
          {creating ? 'Creating...' : 'New note'}
        </button>
      </div>

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
          <h1 className="text-sm font-semibold">Notes</h1>
          {notes.length > 0 && (
            <span className="text-xs text-ink-3">
              {notes.length} {pluralize(notes.length, 'note')}
            </span>
          )}
        </div>

        {notes.length === 0 ? (
          <EmptyState title={search ? 'Nothing matches' : 'No notes yet'}>
            {search
              ? 'No note contains that. Try a different word.'
              : 'Anything worth keeping that is not a task — reference material, meeting minutes, a running scratchpad. These are separate from your categories entirely.'}
          </EmptyState>
        ) : (
          <ul className="m-0 list-none p-0">
            {notes.map((note) => (
              <li key={note.id} className="border-b border-line last:border-b-0">
                <Link
                  to={`/notes/${note.id}`}
                  className="block px-3 py-2.5 transition-colors hover:bg-navy-softer"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span
                      className={`font-medium ${note.title ? 'text-ink' : 'text-ink-3 italic'}`}
                    >
                      {note.display_title}
                    </span>
                    <time className="ml-auto shrink-0 text-xs text-ink-3" dateTime={note.updated_at}>
                      {formatTimestamp(note.updated_at)}
                    </time>
                  </div>
                  {note.preview && (
                    <p className="mt-0.5 truncate text-sm text-ink-3">{note.preview}</p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
